import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const EditBenefits = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [formData, setFormData] = useState({
    has_regularsalary: false,
    amount: '',
    currency_id: '',
    other_currency_name: '',
    provide_accommodation: false,
    other_benefits: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [referenceData, setReferenceData] = useState({
    currencies: []
  });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (staffId) {
      fetchStaffData();
      fetchBenefitsData();
      fetchReferenceData();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      setError('Error retrieving staff information.');
    }
  };

  const fetchBenefitsData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}/benefits`);
      if (response.data) {
        setFormData({
          has_regularsalary: response.data.has_regularsalary || false,
          amount: response.data.amount || '',
          currency_id: response.data.currency_id || '',
          other_currency_name: response.data.other_currency_name || '',
          provide_accommodation: response.data.provide_accommodation || false,
          other_benefits: response.data.other_benefits || ''
        });
      }
    } catch (error) {
      console.error('Error fetching benefits data:', error);
      setError('Error retrieving benefits information.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const response = await api.get('/api/reference/currencies');
      setReferenceData({
        currencies: response.data
      });
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Send the data with the correct field names that backend expects
      const benefitsData = {
        has_regularsalary: formData.has_regularsalary,
        amount: formData.amount || null,
        currency_id: formData.currency_id || null,
        other_currency_name: formData.other_currency_name || null,
        provide_accommodation: formData.provide_accommodation,
        other_benefits: formData.other_benefits || null
      };
      
      await api.put(`/api/staff/${staffId}/benefits`, { benefits: benefitsData });
      setSuccessMessage('Benefits information updated successfully!');
      setTimeout(() => {
        navigate(`/edit-staff/${staffId}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating benefits:', error);
      setError(error.response?.data?.message || 'Error updating benefits information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/edit-staff/${staffId}`);
  };

  if (loading && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800">Loading Benefits Information...</h4>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Edit Benefits Information</h1>
              <p className="text-gray-600">
                Managing benefits for: <strong>{staffData?.fullname}</strong> (ID: {staffData?.staff_id})
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Success and Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h2 className="text-xl font-semibold">Benefits Information</h2>
            <p className="text-blue-100 mt-1">Manage salary and benefits details</p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.has_regularsalary}
                    onChange={(e) => handleInputChange('has_regularsalary', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span>Has Regular Salary</span>
                </label>
              </div>

              {formData.has_regularsalary && (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Salary Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      placeholder="Enter salary amount"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Currency <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.currency_id}
                      onChange={(e) => handleInputChange('currency_id', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                      required
                    >
                      <option value="">Select Currency</option>
                      {referenceData.currencies.map((currency) => (
                        <option key={currency.id} value={currency.id}>
                          {currency.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.currency_id === '3' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Other Currency <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.other_currency_name}
                        onChange={(e) => handleInputChange('other_currency_name', e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                        placeholder="Specify other currency"
                        required
                      />
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.provide_accommodation}
                    onChange={(e) => handleInputChange('provide_accommodation', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span>Provide Accommodation</span>
                </label>
              </div>

              <div className="col-span-full space-y-2">
                <label className="block text-sm font-medium text-gray-700">Other Benefits</label>
                <textarea
                  value={formData.other_benefits}
                  onChange={(e) => handleInputChange('other_benefits', e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                  placeholder="Describe any additional benefits..."
                />
              </div>
            </div>

            {/* Benefits Summary */}
            {(formData.has_regularsalary || formData.provide_accommodation || formData.other_benefits) && (
              <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-sm font-medium text-gray-800 mb-2">Benefits Summary:</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {formData.has_regularsalary && formData.amount && (
                    <li>• Regular Salary: {formData.amount} {referenceData.currencies.find(c => c.id === formData.currency_id)?.name || ''}</li>
                  )}
                  {formData.provide_accommodation && (
                    <li>• Accommodation provided</li>
                  )}
                  {formData.other_benefits && (
                    <li>• Additional Benefits: {formData.other_benefits}</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBenefits;
